// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — per-entity GEOMETRY facet types (T1, split from sceneSummaryProjector)
// ═══════════════════════════════════════════════════════════════════════════
//
// The discriminated `SnapshotEntityGeometry` union (one arm per `drawingEntitySchema` member) + the
// per-entity byte cap (`GEOMETRY_PER_ENTITY_BYTE_CAP`) + the oversized-entity `TruncatedGeometry`
// fallback marker. Extracted from `sceneSummaryProjector.ts` so that seam stays within the 2000-line
// file-size budget (CLAUDE.md "Контроль размера файлов") — the geometry facet (~640 lines of typed
// per-type shapes) is one cohesive responsibility. `sceneSummaryProjector.ts` BARREL-RE-EXPORTS
// everything here (`export * from "./sceneGeometryFacet"`) so every existing import path
// (`from "../ai/sceneSummaryProjector"`) is UNCHANGED (dep-graph: no import-path churn, ПРИОРИТЕТ #2).
//
// LEAF (ПРИОРИТЕТ #2): defines its OWN `Geom*` point types; imports NOTHING (no editorStore /
// agentBridge / drawing-model). The projector (`state/captureEntityGeometry.ts`) imports these TYPES
// + the cap constant from the seam barrel.
// ═══════════════════════════════════════════════════════════════════════════

// ── per-entity GEOMETRY (the `geometry` opt-in facet, T1) ────────────────────────
//
// The discriminated `SnapshotEntityGeometry` union the `entities` route projects under
// `?fields=geometry`: the REAL defining coordinates of each typed `DrawingEntity`, projected
// VERBATIM (ПРИОРИТЕТ #1 — full fidelity, никаких упрощений: no quantization, no inference, no
// flattening of structured points). Every facet mirrors the matching `*EntitySchema` geometry
// fields in packages/drawing-model/src/model.ts (the cited line ranges) — a REQUIRED schema field
// (a `.default(...)`/`.min(...)`/required point) is REQUIRED here; an OPTIONAL schema field
// (`.optional()`) is OPTIONAL here and OMITTED at capture when absent on the source (mirroring the
// metadata facet's optional-vs-present contract). The `kind` discriminant equals the entity `type`
// so a consumer narrows the union exactly as it would the `DrawingEntity` union itself.
//
// SCOPE: every member of the 17-arm `drawingEntitySchema` discriminated union is covered, so a
// `?fields=geometry` read NEVER silently drops a type. The two heaviest arms (Hatch boundary paths /
// Multileader leaderRoots) project a COMPACT-but-FULL shape (the defining vertices/roots), NOT a
// truncated summary — they are byte-gated by the `?fields=geometry` opt-in + `?fullField=geometry`
// uncapped single-item retrieval, never silently summarized (ПРИОРИТЕТ #1).
//
// A `Viewport` is also an entity (it appears on the entities route too); its geometry facet projects
// the paper-space frame (`center`/`width`/`height`) — the RICH viewport view-state lives on the
// dedicated `viewports` route, not here (the entities route is the metadata+frame view of a VP).

/** A 2D point (`{x,y}`) — mirrors `drawingPointSchema`. Projected verbatim. */
export interface GeomPoint2D {
  readonly x: number;
  readonly y: number;
}

/**
 * A 3D point with an OPTIONAL `z` — mirrors `drawingPointOptionalZSchema` (LINE/POINT/CIRCLE/ARC/
 * INSERT/ARRAYRECT base points carry `z?`). `z` is OMITTED when the source point omitted it (a bare
 * 2D `{x,y}` literal) — never synthesized to 0.
 */
export interface GeomPointOptionalZ {
  readonly x: number;
  readonly y: number;
  readonly z?: number;
}

/** A 3D point with a REQUIRED `z` — mirrors `drawingPoint3DSchema` (extrusion normals, spline points). */
export interface GeomPoint3D {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** LINE geometry (model.ts lineEntitySchema, ~L214): 3D endpoints + optional extrusion/thickness. */
export interface LineGeometry {
  readonly kind: "Line";
  readonly start: GeomPointOptionalZ;
  readonly end: GeomPointOptionalZ;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/** POINT geometry (pointEntitySchema, ~L239): 3D location + UCS rotation + optional extrusion. */
export interface PointGeometry {
  readonly kind: "Point";
  readonly location: GeomPointOptionalZ;
  readonly rotation: number;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/** CIRCLE geometry (circleEntitySchema, ~L255): 3D center + radius + optional extrusion. */
export interface CircleGeometry {
  readonly kind: "Circle";
  readonly center: GeomPointOptionalZ;
  readonly radius: number;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/**
 * ARC geometry (arcEntitySchema, ~L271): 3D center + radius + start/end angle + optional
 * extrusion. `startAngle`/`endAngle` are DEGREES (DXF ARC group 50/51, matches
 * `arcEntitySchema` verbatim) — NOT radians, despite the field-name pattern elsewhere on this
 * file (e.g. Ellipse's `startParam`/`endParam` ARE radians per the DXF ELLIPSE spec). Verified
 * against `boundaryDetection.ts`'s `degToRad(entity.startAngle)` and
 * `sceneGeometryBuilders.ts`'s `buildArcSceneGeometry(startDeg, endDeg)`, both of which convert
 * the SAME model field from degrees before treating it as radians. A consumer that needs
 * radians (e.g. `ai/sceneQuery/osnapQuery.ts`'s `toSceneGeometry`) must convert explicitly.
 */
export interface ArcGeometry {
  readonly kind: "Arc";
  readonly center: GeomPointOptionalZ;
  readonly radius: number;
  readonly startAngle: number;
  readonly endAngle: number;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/**
 * ELLIPSE geometry (ellipseEntitySchema, ~L298): center + major-axis end VECTOR (relative to center)
 * + minor/major ratio + start/end param (radians) + extrusion (a `.default` → required) + thickness.
 * SL-15 — center + major-axis are 3D with an OPTIONAL z (drawingPointOptionalZSchema, like Circle/Arc):
 * an elevated / tilted ellipse projects its real z, a planar one omits it (never synthesized to 0).
 */
export interface EllipseGeometry {
  readonly kind: "Ellipse";
  readonly center: GeomPointOptionalZ;
  readonly majorAxisEndpoint: GeomPointOptionalZ;
  readonly ratio: number;
  readonly startParam: number;
  readonly endParam: number;
  readonly extrusion: GeomPoint3D;
  readonly thickness: number;
}

/**
 * SPLINE geometry (splineEntitySchema, ~L335): the FULL NURBS / fit-point definition — degree, the
 * 5 flag bits, fit points + optional tangents, control points + knots + weights, and the 3
 * tolerances. Projected VERBATIM (the full control-point list, NOT a sampled polyline — ПРИОРИТЕТ #1
 * forbids the "one control point" / quadratic simplification). Byte-gated by the geometry opt-in.
 */
export interface SplineGeometry {
  readonly kind: "Spline";
  readonly degree: number;
  readonly closed: boolean;
  readonly periodic: boolean;
  readonly rational: boolean;
  readonly planar: boolean;
  readonly linear?: boolean;
  readonly fitPoints: readonly GeomPoint3D[];
  readonly startTangent?: GeomPoint3D;
  readonly endTangent?: GeomPoint3D;
  readonly controlPoints: readonly GeomPoint3D[];
  readonly knots: readonly number[];
  readonly weights: readonly number[];
  readonly knotTolerance: number;
  readonly controlPointTolerance: number;
  readonly fitTolerance: number;
  // SL-15 — the SPLINE normal (DXF 210/220/230); present ONLY for a planar
  // spline in a tilted plane (absent ⇒ the (0,0,1) WCS default).
  readonly extrusion?: GeomPoint3D;
}

/** One LWPOLYLINE vertex (polylineVertexSchema, ~L363): x/y + bulge + per-vertex widths + optional 91 id. */
export interface GeomPolylineVertex {
  readonly x: number;
  readonly y: number;
  readonly bulge: number;
  readonly startWidth: number;
  readonly endWidth: number;
  readonly vertexId?: number;
}

/**
 * LWPOLYLINE geometry (lwpolylineEntitySchema, ~L376): the FULL vertex list (with per-vertex bulge +
 * start/end width — the arc-segment fidelity ПРИОРИТЕТ #1 mandates), closed/plinegen flags, constant
 * width, elevation, thickness, optional extrusion. Byte-gated by the geometry opt-in.
 */
export interface LwpolylineGeometry {
  readonly kind: "LWPOLYLINE";
  readonly vertices: readonly GeomPolylineVertex[];
  readonly closed: boolean;
  readonly plinegen?: boolean;
  readonly constantWidth: number;
  readonly elevation: number;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/**
 * TEXT geometry (textEntitySchema, ~L396): insertion (+ optional alignment) point, height, rotation,
 * the text CONTENTS (the agent reads the actual string to find "the wall label"), style + the format
 * scalars (width factor / oblique / justification / generation flags), thickness, extrusion.
 */
export interface TextGeometry {
  readonly kind: "Text";
  readonly insertionPoint: GeomPoint2D;
  readonly alignmentPoint?: GeomPoint2D;
  readonly height: number;
  readonly rotation: number;
  readonly contents: string;
  readonly styleName: string;
  readonly widthFactor: number;
  readonly obliqueAngle: number;
  readonly hJustification: number;
  readonly vJustification: number;
  readonly thickness: number;
  readonly generationFlags: number;
  readonly extrusion: GeomPoint3D;
}

/**
 * MTEXT geometry (mtextEntitySchema, ~L431): insertion point, nominal height, reference width, the
 * attachment / direction codes, the CONTENTS, style, rotation, line-spacing, the x-axis direction,
 * optional extrusion, the resolved text-box EXTENTS (`horizontalWidth`/`verticalHeight`), and the
 * COLUMN layout (type / count / width / gutter / per-column heights / flow / auto-height) that defines
 * the multi-column text placement (@codex T1 P2 — these geometry-defining placement fields were
 * missing; only the background-fill APPEARANCE scalars stay off this facet as render metadata).
 */
export interface MtextGeometry {
  readonly kind: "MText";
  readonly insertionPoint: GeomPoint2D;
  readonly nominalHeight: number;
  readonly referenceWidth: number;
  readonly attachmentPoint: number;
  readonly drawingDirection: number;
  readonly contents: string;
  readonly styleName: string;
  readonly rotation: number;
  readonly lineSpacingStyle: number;
  readonly lineSpacingFactor: number;
  readonly xAxisDirection: GeomPoint3D;
  readonly extrusion?: GeomPoint3D;
  /** The resolved text-box width extent (the actual drawn box, vs the `referenceWidth` request). */
  readonly horizontalWidth: number;
  /** The resolved text-box height extent. */
  readonly verticalHeight: number;
  /** Column layout type (0 = none, 1 = static, 2 = dynamic). */
  readonly columnType: number;
  /** Column count. */
  readonly columnCount: number;
  /** Column width. */
  readonly columnWidth: number;
  /** Gutter between columns. */
  readonly columnGutter: number;
  /** Per-column heights (a dynamic-column layout). */
  readonly columnHeights: readonly number[];
  /** Whether column flow is reversed. */
  readonly columnFlowReversed: boolean;
  /** Whether columns auto-size their height. */
  readonly columnAutoheight: boolean;
}

/**
 * DIMENSION geometry (dimensionEntitySchema, ~L513): the def point + text midpoint + the optional
 * extension-line / third / arc / insertion points, the dimension type code, the measurement value,
 * the dimension text, and the angle scalars. The defining points the agent needs to understand /
 * relocate a dimension; the per-DIMVAR style overrides live on the full typed model.
 *
 * DIMENSION is an OCS-planar entity (like Line/Circle/Arc/LWPOLYLINE/Hatch): it carries the OCS
 * `extrusion` normal (210/220/230) + `thickness` (39), both OPTIONAL on the schema (@codex T1 P2 —
 * the verbatim projection omitted them). `textAngleRelativeToDimLine` (code 53) is the text-placement
 * angle, a geometry sibling of the already-projected `textRotation`/`obliqueAngle`.
 */
export interface DimensionGeometry {
  readonly kind: "Dimension";
  readonly dimensionType: number;
  readonly defPoint: GeomPoint2D;
  readonly textMidpoint: GeomPoint2D;
  readonly dimensionText: string;
  readonly measurement: number;
  readonly extLine1Point?: GeomPoint2D;
  readonly extLine2Point?: GeomPoint2D;
  readonly thirdPoint?: GeomPoint3D;
  readonly arcPoint?: GeomPoint3D;
  readonly insertionPoint?: GeomPoint3D;
  readonly linearAngle: number;
  readonly textRotation: number;
  readonly obliqueAngle: number;
  readonly textAngleRelativeToDimLine: number;
  /** MIRRTEXT glyph-handedness — OPTIONAL; render-only (AcDbDimension has no dedicated group). */
  readonly textMirrored?: boolean;
  readonly leaderLength: number;
  /** OCS extrusion normal (210/220/230) — OPTIONAL on the schema; absent ⇒ the (0,0,1) default. */
  readonly extrusion?: GeomPoint3D;
  /** OCS thickness (group 39) — OPTIONAL on the schema; absent ⇒ no extrusion thickness. */
  readonly thickness?: number;
  // ── Text-placement geometry (schema gate reveal) — the dim TEXT size + attachment + line layout
  //    that, with `textRotation`/`obliqueAngle`/`textMidpoint`, define WHERE/HOW BIG the dimension
  //    text draws (the agent reading/relocating a dim needs them). The DIMSTYLE NAME / per-DIMVAR
  //    `styleOverrides` / per-scale `annotativeContexts` / `anonymousBlockName` / `version` are
  //    style/linkage metadata on the full typed model (documented (c)-deferral in the gate).
  /** Dimension text height (code 140 family) — the SIZE of the dim text. `.default(0)`. */
  readonly textHeight: number;
  /** Text attachment point (code 71) — WHERE the text sits relative to the dim line. `.default(5)`. */
  readonly attachmentPoint: number;
  /** Text line-spacing style (code 72) — OPTIONAL; multi-line dim-text layout (mirrors MText). */
  readonly lineSpacingStyle?: number;
  /** Text line-spacing factor (code 41) — OPTIONAL; multi-line dim-text layout. */
  readonly lineSpacingFactor?: number;
}

/**
 * One MLEADER break-point segment (mleaderBreakPointSchema, ~L597): a `{start,end}` 3D-optional-z pair
 * marking where a leader line is BROKEN (crosses another entity). A LeaderLine break carries the
 * `breakIndex` (group 90); a LeaderData break omits it. Projected verbatim (@codex T1 P2 — the leader
 * break-segment geometry was missing).
 */
export interface GeomMleaderBreakPoint {
  readonly start: GeomPointOptionalZ;
  readonly end: GeomPointOptionalZ;
  readonly breakIndex?: number;
}

/**
 * MULTILEADER geometry (multileaderEntitySchema, ~L837): the text location / optional base point, the
 * text contents + height/rotation, and the FULL leader-root → node → line → vertex hierarchy. The
 * `leaderRoots[]` is projected as its complete defining VERTEX structure (`nodes[].lines[].vertices`,
 * mirroring `mleaderLeaderRootSchema` → `mleaderLeaderNodeSchema` → `mleaderLeaderLineSchema`) — NOT
 * one leader / one node / a single flattened polyline (ПРИОРИТЕТ #1 forbids the single-instance
 * simplification). Each node carries its optional dogleg vector + dogleg length + last-leader point;
 * each leader LINE and each NODE carries its `breakPoints[]` break-segment geometry (@codex T1 P2 —
 * the break segments were missing). Byte-gated by the geometry opt-in.
 *
 * The structural CONTEXT_DATA mirror (`contextData`/`secondaryContexts`) is DELIBERATELY OMITTED — it
 * is the byte-faithful round-trip writer's full field set (a deeply-nested handle/raw-laden graph),
 * NOT the agent's geometry read-model. `leaderRoots[]` IS the defining leader geometry; the
 * CONTEXT_DATA block is its parallel structural representation the writer round-trips, so it stays on
 * the full typed model (mirroring the Viewport rich-state route split). See the deferral note in
 * captureEntityGeometry's Multileader arm.
 */
export interface MultileaderGeometry {
  readonly kind: "Multileader";
  readonly textLocation: GeomPoint2D;
  readonly basePoint?: GeomPoint2D;
  readonly textContents: string;
  readonly textHeight: number;
  readonly textRotation: number;
  /** MIRRTEXT glyph-handedness — OPTIONAL; render-only, mirrors DimensionGeometry's textMirrored. */
  readonly textMirrored?: boolean;
  readonly contentType: number;
  // ── BLOCK-content geometry (@codex T1 round-2 P2 — finding :289). When `contentType === 1` the
  //    MLEADER's drawn content is a BLOCK reference; its placed shape is defined by the block NAME +
  //    per-instance scale/rotation + the block-connection type + the readable ATTRIB payload — the
  //    direct analogue of a Block INSERT's geometry, NOT covered by the text-content fields above.
  //    Projected verbatim like the text fields (both are always-present `.default(...)` save the
  //    optional `blockConnectionType`); a text-content MLEADER carries the schema defaults
  //    (`blockName: ""`, scale 1, rotation 0, no attribute refs). The block-content COLOUR /
  //    344-handle (`blockColor`/`blockRecordHandle`) are appearance/linkage metadata (documented
  //    (c)-deferral in the gate). Each attribute ref's `attDefHandle` is a 330 soft-pointer; the
  //    readable payload is its `index`/`width`/`value`.
  readonly blockName: string;
  readonly blockScale: number;
  readonly blockRotation: number;
  /** AcDbMLeader body 176 (block connection type) — OPTIONAL; absence preserved (no `.default`). */
  readonly blockConnectionType?: number;
  readonly blockAttributeReferences: readonly {
    readonly attDefHandle: string;
    readonly index: number;
    readonly width: number;
    readonly value: string;
  }[];
  /**
   * Each leader root → its leader-DATA nodes → each node's leader LINES → each line's vertex list
   * (the defining polyline of the leader) + the line's `breakPoints[]`. Each node carries its optional
   * `doglegVector` / `doglegLength` / `lastLeaderPoint` + its own LeaderData `breakPoints[]`. The full
   * `mleaderLeaderRootSchema` hierarchy's GEOMETRY, verbatim.
   */
  readonly leaderRoots: readonly {
    readonly nodes: readonly {
      readonly doglegVector?: GeomPoint3D;
      readonly doglegLength?: number;
      readonly lastLeaderPoint?: GeomPoint3D;
      readonly breakPoints?: readonly GeomMleaderBreakPoint[];
      readonly lines: readonly {
        readonly vertices: readonly GeomPointOptionalZ[];
        readonly breakPoints?: readonly GeomMleaderBreakPoint[];
      }[];
    }[];
  }[];
}

/**
 * REVISION CLOUD geometry (revisionCloudEntitySchema, ~L946): the FULL vertex list (≥3, with bulge /
 * widths like an LWPOLYLINE — REVCLOUD is an LWPOLYLINE-derived entity), closed/plinegen flags (both
 * group-70 bits, mirroring LWPOLYLINE), the arc-length scalars, the creation mode + style, constant
 * width, elevation, thickness, optional extrusion.
 */
export interface RevisionCloudGeometry {
  readonly kind: "Revision Cloud";
  readonly vertices: readonly GeomPolylineVertex[];
  readonly closed: boolean;
  readonly plinegen?: boolean;
  readonly arcChordLength: number;
  readonly approximateArcLength: number;
  readonly revisionCloudStyle: string;
  readonly creationMode: "Rectangular" | "Polygonal" | "Object";
  readonly constantWidth: number;
  readonly elevation: number;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/** WIPEOUT geometry (wipeoutEntitySchema, ~L993): the closed boundary polygon (≥3 2D vertices) + frame flag + clip mode (290: 0 outside / 1 inside). */
export interface WipeoutGeometry {
  readonly kind: "Wipeout";
  readonly vertices: readonly GeomPoint2D[];
  readonly frame: boolean;
  readonly clipMode: 0 | 1;
}

/** One HATCH boundary edge (hatchBoundaryEdgeSchema, ~L1000): a discriminated line / arc / ellipticArc edge. */
export type GeomHatchEdge =
  | { readonly edgeType: "line"; readonly start: GeomPoint2D; readonly end: GeomPoint2D }
  | {
      readonly edgeType: "arc";
      readonly center: GeomPoint2D;
      readonly radius: number;
      readonly startAngle: number;
      readonly endAngle: number;
      readonly counterclockwise: boolean;
    }
  | {
      readonly edgeType: "ellipticArc";
      readonly center: GeomPoint2D;
      readonly majorAxisEnd: GeomPoint2D;
      readonly minorRatio: number;
      readonly startAngle: number;
      readonly endAngle: number;
      readonly counterclockwise: boolean;
    };

/**
 * One custom HATCH pattern-definition line (hatchPatternLineSchema, ~L1089): a PAT family line —
 * `angle` (53), `base` {x,y} (43/44), `offset` {x,y} (45/46 — STRUCTURED, never flattened), and the
 * `dashLengths[]` (49; +dash / −gap / 0 dot). Present only for a custom (non-predefined) pattern.
 * Projected verbatim (@codex T1 P2 — the custom PAT definition was missing).
 */
export interface GeomHatchPatternLine {
  readonly angle: number;
  readonly base: GeomPoint2D;
  readonly offset: GeomPoint2D;
  readonly dashLengths: readonly number[];
}

/**
 * HATCH geometry (hatchEntitySchema, ~L1096): the FULL boundary-path geometry (each path's defining
 * edges) + the pattern name / solid+associative flags / pattern angle+scale / elevation. The boundary
 * paths are projected as their defining edge geometry (line / arc / ellipticArc), NOT a flattened
 * polyline — ПРИОРИТЕТ #1. This is the HEAVIEST geometry arm; it is byte-gated by the `?fields=geometry`
 * opt-in + `?fullField=geometry` uncapped retrieval, never silently summarized.
 *
 * The full WCS elevation POINT is `{elevationX?, elevationY?, elevation}` (groups 10/20/30) — `elevation`
 * is the Z scalar; `elevationX`/`elevationY` (the X/Y of the point, usually 0) are OPTIONAL and complete
 * it (@codex T1 P2 — only Z was projected). `seedPoints[]` are the pattern seed points (real 2D coords).
 * `customPatternLines[]` is the custom PAT family definition (@codex T1 P2). The gradient-fill /
 * pattern-sampling scalars (gradient / pixelSize / hatchStyle) are appearance metadata on the full
 * typed model, not defining geometry.
 */
export interface HatchGeometry {
  readonly kind: "Hatch";
  readonly patternName: string;
  readonly solidFill: boolean;
  readonly associative: boolean;
  readonly patternAngle: number;
  readonly patternScale: number;
  /** The Z component (group 30) of the WCS elevation point. */
  readonly elevation: number;
  /** The X component (group 10) of the WCS elevation point — OPTIONAL (omitted when 0). */
  readonly elevationX?: number;
  /** The Y component (group 20) of the WCS elevation point — OPTIONAL (omitted when 0). */
  readonly elevationY?: number;
  readonly boundaryPaths: readonly {
    readonly pathTypeFlag: number;
    readonly edges: readonly GeomHatchEdge[];
    /**
     * Associative source-boundary refs (@codex T1 round-2 P2 — finding :345): the group-330 soft-
     * pointer handles of the SOURCE entities whose geometry DEFINES this path (an associative hatch,
     * group 71 = 1). The defining-geometry reference list — the read-view of the same field T9 defect-5
     * handled on export. OPTIONAL (absent for a non-associative path; an empty array when the path
     * declared 0 sources — `sourceBoundaryHandles: z.array(z.string()).optional()`). Omitted at
     * capture when absent on the source.
     */
    readonly sourceBoundaryHandles?: readonly string[];
  }[];
  /** The pattern seed points (real 2D coords) — `.default([])`, always present (possibly empty). */
  readonly seedPoints: readonly GeomPoint2D[];
  /** The custom PAT pattern-definition lines — OPTIONAL (present only for a custom pattern). */
  readonly customPatternLines?: readonly GeomHatchPatternLine[];
  readonly extrusion?: GeomPoint3D;
}

/**
 * BLOCK / INSERT geometry (blockInsertEntitySchema, ~L1182): the block NAME (which block is placed),
 * the 3D insertion point, the per-axis scale, rotation, the MINSERT column/row grid (count+spacing),
 * the extrusion, and the resolved ATTRIB tag→value pairs (the agent reads attribute text). NO block
 * content flattening (the children live in the block definition — the `blocks` route / a block read).
 */
export interface BlockGeometry {
  readonly kind: "Block";
  readonly blockName: string;
  readonly insertionPoint: GeomPointOptionalZ;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly scaleZ: number;
  readonly rotation: number;
  readonly columnCount: number;
  readonly rowCount: number;
  readonly columnSpacing: number;
  readonly rowSpacing: number;
  readonly extrusion: GeomPoint3D;
  /**
   * The INSERT's ATTRIB children — the readable `tag`/`value` text PLUS the per-ATTRIB draw-overrides
   * `overridePosition` (where) / `overrideRotation` / `overrideHeight` / `overrideGenerationFlags`
   * (how the attribute text is actually drawn, vs inherited from the ATTDEF) — each OPTIONAL, present
   * only when the ATTRIB overrides the block default (@codex T1 P2 — the overrides were missing).
   * `overrideGenerationFlags` (@codex round-2, round-17 local read-only follow-up P2 — "Expose
   * attribute handedness in geometry snapshots") is DXF group 71 (bit 2 BACKWARD / bit 4 UPSIDE_DOWN),
   * the mirror-handedness companion to `overrideRotation` — without it, a WORLD-placed attribute
   * mirrored via MIRROR/EATTEDIT and an identical unmirrored one produced IDENTICAL agent-facing
   * geometry snapshots even though their rendered glyph geometry (and PDF/DXF export) genuinely
   * differ. The non-geometry `overrideStyle` / `invisible` / `handle` / `rawGroups` stay on the full
   * typed model.
   */
  readonly attributes: readonly {
    readonly tag: string;
    readonly value: string;
    readonly overridePosition?: GeomPoint2D;
    readonly overrideRotation?: number;
    readonly overrideHeight?: number;
    readonly overrideGenerationFlags?: number;
  }[];
  // ── DYNAMIC-BLOCK evaluated state (@codex T1 round-2 P2 — finding :422). The placed/evaluated shape
  //    of a dynamic-block INSERT depends on its per-instance dynamic PARAMETERS + visibility STATE, not
  //    on `blockName`+transform alone — the preview/evaluator path reads exactly these. Projected so the
  //    agent SEES which parameter set / visibility a given INSERT is evaluated under (an absent value =
  //    inherit the block default). `customProperties` is the active runtime param map (`.default({})`,
  //    always present — possibly empty for a static block). `visibilityState` is the active visibility-
  //    state name (OPTIONAL — absent for a static block). `representationOverrides` is the per-INSERT
  //    DIVERGENCE the evaluator applies on top of the block defaults: the explicit `parameterOverrides`
  //    map + the `currentVisibilityStateId` the user diverged on. The 102-XDICT representation linkage
  //    (`representationEtag`/`representationDataHandle`) is opaque round-trip handle metadata
  //    (documented (c)-deferral); `annotationScales`/`annotativeContexts`/`creationScaleName` are
  //    per-scale appearance metadata (also (c)-deferred).
  /** Active dynamic-block parameter values (`customProperties` — `.default({})`, always present). */
  readonly customProperties: Readonly<Record<string, string | number | boolean>>;
  /** Active dynamic-block visibility-state name — OPTIONAL (absent for a static block). */
  readonly visibilityState?: string;
  /** Per-INSERT dynamic divergence (param/visibility overrides) the evaluator applies — OPTIONAL. */
  readonly representationOverrides?: {
    readonly parameterOverrides: Readonly<Record<string, string | number | boolean>>;
    readonly currentVisibilityStateId?: string;
  };
}

/**
 * RECTANGULAR ASSOCIATIVE ARRAY geometry (arrayRectEntitySchema, ~L1279): the base point, the row /
 * column grid (count + spacing + total), the axis angle, the associative flag, the optional 3D level
 * stack (level count/spacing/total + per-row Z elevation), the optional 3D axis-direction vectors
 * (`xAxisDirection`/`yAxisDirection` — the real column/row axes vs the scalar `axisAngle`) +
 * `basePlaneNormal` (the array base-plane OCS normal), and the per-item `itemRotation` (@codex T1 P2 —
 * the 3D axes / row elevation were missing). The full AcDbAssocArray graph (per-item evaluated
 * transforms / persisted item records / driving expressions / handles) lives on the typed model — it
 * is the assoc-graph round-trip, NOT the defining grid the agent reads / edits.
 */
export interface ArrayRectGeometry {
  readonly kind: "Rectangular Associative Array";
  readonly basePoint: GeomPointOptionalZ;
  readonly rowCount: number;
  readonly columnCount: number;
  readonly rowSpacing: number;
  readonly columnSpacing: number;
  readonly rowTotal: number;
  readonly columnTotal: number;
  readonly axisAngle: number;
  readonly associative: boolean;
  /**
   * The SOURCE-entity id list (@codex T1 round-2 P2 — finding :439): the ids of the original
   * pre-array entities whose geometry is REPLICATED across the grid (the array's defining content).
   * `sourceEntityIds: z.array(z.string()).default([])` — always present (empty for an array that
   * carried no source list). The evaluated/persisted per-item assoc graph (`itemTransforms`/
   * `assocItems`/`itemSourceMapping`/`itemOverrides`/expression strings/`assocHandles`) is the
   * round-trip assoc-graph on the typed model (documented (c)-deferral in the gate).
   */
  readonly sourceEntityIds: readonly string[];
  readonly levelCount?: number;
  readonly levelSpacing?: number;
  /** The Z-stack total extent (sibling of rowTotal/columnTotal) — OPTIONAL (3D arrays only). */
  readonly levelTotal?: number;
  /** Per-row Z elevation increment (3D rectangular array) — OPTIONAL. */
  readonly rowElevation?: number;
  /** The column (X) axis direction VECTOR — OPTIONAL (a real assoc array's true axis vs `axisAngle`). */
  readonly xAxisDirection?: GeomPoint3D;
  /** The row (Y) axis direction VECTOR — OPTIONAL. */
  readonly yAxisDirection?: GeomPoint3D;
  /** The OCS normal of the array's base plane — OPTIONAL. */
  readonly basePlaneNormal?: GeomPoint3D;
  /** Per-item rotation applied to every generated copy (radians) — OPTIONAL. */
  readonly itemRotation?: number;
}

/**
 * VIEWPORT geometry (viewportEntitySchema, ~L1384): the paper-space FRAME — center / width / height —
 * the placement an agent reads on the entities route. The rich view-state (viewCenter / viewHeight /
 * frozen layers / overrides) lives on the dedicated `viewports` route, not duplicated here.
 */
export interface ViewportGeometry {
  readonly kind: "Viewport";
  readonly center: GeomPoint2D;
  readonly width: number;
  readonly height: number;
}

/**
 * The per-entity geometry BYTE CAP (@codex T1 round-2 P2 — finding :1411). `?fullField=geometry`
 * exposes one entity's geometry as a SINGLE-ITEM uncapped read (limit forced to 1, so re-reading
 * with a smaller `limit` cannot shrink the page). A single colossal entity — a 10k-vertex polyline,
 * a complex hatch with thousands of boundary edges, a many-control-point spline — would serialize
 * past the companion's per-frame ceiling (`apps/node-companion/src/readBudget.ts`
 * `WS_MAX_PAYLOAD_BYTES = 4 MiB`; the page budget `RESPONSE_BYTE_BUDGET = WS + 128 KiB`). The
 * companion then HARD-REJECTS the over-budget page (`stateRoute.ts` `byte-budget-exceeded`) and,
 * because `fullField` is already at limit=1, the read is UNRECOVERABLE — the agent gets a 4xx, never
 * the geometry.
 *
 * So the PROJECTOR caps a single entity's geometry by SIZE: if `JSON.stringify(geometry)` exceeds
 * this cap, the heavy defining arrays are DROPPED and a `TruncatedGeometry` marker is emitted
 * instead — the entity's `type`, the serialized byte size, the cap, and the per-array element COUNTS
 * (so the agent learns WHAT is too large) + a `narrowerQuery` hint. The read NEVER hard-rejects; the
 * agent gets a bounded, usable signal and can re-query a narrower slice. The cap MATCHES the
 * companion's frame ceiling MINUS a reserve for the response/item-wrapper envelope (the budget is
 * `WS + 128 KiB`, and a single-item page = envelope + one item, so capping the per-entity geometry
 * strictly BELOW `WS_MAX_PAYLOAD_BYTES` guarantees the assembled page fits `RESPONSE_BYTE_BUDGET`
 * with the metadata sibling fields). Mirrors `readBudget.ts`'s magnitude WITHOUT importing it (that
 * is a node-companion module — a cross-app runtime import would add a dep-graph edge; the value is
 * re-declared here as the browser-side truth and asserted equal to the companion constant by the
 * `readBudget` test that imports BOTH). 3.5 MiB leaves ≥0.6 MiB of the 4 MiB frame for the row's
 * metadata fields + the response/item envelope.
 */
export const GEOMETRY_PER_ENTITY_BYTE_CAP = 3.5 * 1024 * 1024;

/**
 * The marker a `?fields=geometry` / `?fullField=geometry` read emits IN PLACE of a single entity's
 * geometry when that geometry exceeds `GEOMETRY_PER_ENTITY_BYTE_CAP` (finding :1411 — the read never
 * hard-rejects on an oversized single item). `kind: "Truncated"` is a NON-entity discriminant (no
 * `drawingEntitySchema` arm has it), so a consumer narrows it out of the per-type union cleanly. It
 * carries the entity's real `type`, the serialized `byteSize` (> the cap), the `cap`, the dropped
 * heavy arrays' element `counts` (so the agent sees the magnitude of what was withheld), and a
 * `narrowerQuery` hint pointing at a usable per-field read.
 */
export interface TruncatedGeometry {
  readonly kind: "Truncated";
  /** The entity's real `type` (the arm that WOULD have been projected). */
  readonly type: string;
  /** The full geometry's serialized size in bytes (exceeds `cap`). */
  readonly byteSize: number;
  /** The cap that was exceeded (`GEOMETRY_PER_ENTITY_BYTE_CAP`). */
  readonly cap: number;
  /** `true` — an explicit marker that the geometry was truncated (not absent / not empty). */
  readonly truncated: true;
  /** Per-dropped-array element counts (e.g. `{ vertices: 10234 }`) — the magnitude withheld. */
  readonly droppedArrayCounts: Readonly<Record<string, number>>;
  /** A hint at a usable narrower read (the heavy geometry is too large for a single page). */
  readonly narrowerQuery: string;
}

/**
 * One heavy-POLYLINE `VERTEX` record (`polylineVertexRecordSchema`): its own
 * handle plus the full 3-D location and the per-vertex width / bulge / flag /
 * face-index payload. Distinct from {@link GeomPolylineVertex} (the flat
 * LWPOLYLINE tuple) because a heavy vertex genuinely carries more: a real Z, a
 * DXF handle, curve/spline-fit flags, a curve-fit tangent, and — on a
 * poly-face FACE record — the vertex indices forming the face.
 */
export interface GeomHeavyPolylineVertex {
  readonly handle: string;
  readonly location: GeomPoint3D;
  readonly startWidth?: number;
  readonly endWidth?: number;
  readonly bulge?: number;
  readonly flags?: number;
  readonly curveFitTangent?: number;
  readonly faceVertexIndices?: readonly number[];
  readonly vertexId?: number;
}

/**
 * Legacy heavy POLYLINE geometry (`polylineEntitySchema`): the group-70 flags
 * bitfield that discriminates 2D polyline / 3D polyline / polygon mesh /
 * poly-face mesh, the resolved `subtype` label, the full VERTEX list, the
 * default widths, elevation, the mesh counts and smooth-surface parameters.
 */
export interface PolylineGeometry {
  readonly kind: "Polyline";
  readonly subtype: "2d" | "3d" | "polygon-mesh" | "polyface-mesh";
  readonly flags: number;
  readonly closed: boolean;
  readonly vertices: readonly GeomHeavyPolylineVertex[];
  readonly elevation: GeomPoint3D;
  readonly defaultStartWidth?: number;
  readonly defaultEndWidth?: number;
  readonly meshMCount?: number;
  readonly meshNCount?: number;
  readonly smoothSurfaceMDensity?: number;
  readonly smoothSurfaceNDensity?: number;
  readonly surfaceType?: number;
  readonly thickness: number;
  readonly extrusion?: GeomPoint3D;
}

/**
 * ACAD_TABLE geometry (`tableEntitySchema`): the placement plus the table's
 * shape. The per-cell records are deliberately NOT projected — they are
 * retained verbatim for round-trip and carry no typed model in this change
 * (see `tableEntitySchema`'s boundary doc), so exposing them here would ship
 * an agent-readable structure nothing can act on. `blockName` is the anonymous
 * `*T<N>` block that holds the rendered table graphics.
 */
export interface TableGeometry {
  readonly kind: "Table";
  readonly blockName: string;
  readonly insertionPoint: GeomPoint3D;
  readonly horizontalDirection?: GeomPoint3D;
  readonly rotation: number;
  readonly rowCount?: number;
  readonly columnCount?: number;
  readonly rowHeights: readonly number[];
  readonly columnWidths: readonly number[];
}

/**
 * OLE2FRAME geometry (`oleFrameEntitySchema`): the two WCS frame corners plus
 * the OLE record's descriptors. The embedded payload is never decoded (see
 * that schema's doc), so only its byte count is reported — an agent must not
 * be led to believe it can read the picture.
 */
export interface OleFrameGeometry {
  readonly kind: "OLE Frame";
  readonly upperLeft: GeomPoint3D;
  readonly lowerRight: GeomPoint3D;
  readonly objectDescription?: string;
  readonly oleObjectType?: number;
  readonly drawAspect?: number;
  readonly binaryByteCount?: number;
}

/**
 * The discriminated per-entity geometry union (the `SnapshotEntityRef.geometry` facet, T1). One arm
 * per `drawingEntitySchema` member (20 arms) — the `kind` discriminant equals the entity `type`. A
 * `?fields=geometry` read projects exactly the arm matching the entity's type; an exhaustive switch
 * over `kind` covers every arm (a new entity type added to `drawingEntitySchema` without an arm here
 * surfaces as a tsc error at the capture projector's `assertNever`, by-construction completeness).
 */
export type SnapshotEntityGeometry =
  | LineGeometry
  | PointGeometry
  | CircleGeometry
  | ArcGeometry
  | EllipseGeometry
  | SplineGeometry
  | LwpolylineGeometry
  | TextGeometry
  | MtextGeometry
  | DimensionGeometry
  | MultileaderGeometry
  | RevisionCloudGeometry
  | WipeoutGeometry
  | HatchGeometry
  | BlockGeometry
  | ArrayRectGeometry
  | ViewportGeometry
  | PolylineGeometry
  | TableGeometry
  | OleFrameGeometry
  // The oversized-entity fallback (finding :1411) — emitted IN PLACE of a per-type arm when the
  // geometry exceeds `GEOMETRY_PER_ENTITY_BYTE_CAP`, so a single huge entity never hard-rejects the
  // page. `kind: "Truncated"` is NOT a `drawingEntitySchema` type (the consumer narrows it out).
  | TruncatedGeometry;
